<script setup lang="ts">
import {
  addFrames,
  purge,
  react,
  relayout,
  type Data,
  type Layout,
  type PlotlyHTMLElement,
} from "plotly.js-dist-min";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

import { isDeepEqual } from "@/services/utils";

const { spec, displayModeBar = true } = defineProps<{
  spec: { data: any; layout: any; frames: any };
  displayModeBar?: boolean;
}>();
const emit = defineEmits<{ dataChange: [data: Data[]] }>();

const container = ref<HTMLDivElement>();

function makeLayout(): Partial<Layout> {
  const width = container.value?.clientWidth;
  let height = container.value?.clientHeight;
  const parent = container.value?.parentElement;
  if (parent) {
    const styles = getComputedStyle(parent);
    const top = parseFloat(styles.getPropertyValue("padding-top"));
    const bottom = parseFloat(styles.getPropertyValue("padding-bottom"));
    height = Math.round(parent.clientHeight - top - bottom);
  }
  return {
    ...spec.layout,
    paper_bgcolor: "transparent",
    width,
    height,
    font: {
      family: "GeistMono, monospace",
    },
  };
}

function onPlotRestyle() {
  if (_plot !== null) {
    emit("dataChange", _plot.data);
  }
}

function removePlotListeners() {
  _plot?.removeAllListeners("plotly_restyle");
  _plot = null;
}

let _plot: PlotlyHTMLElement | null = null;
async function buildPlot(containerValue: HTMLDivElement, plotData: any) {
  removePlotListeners();
  _plot = await react(containerValue, plotData, makeLayout(), {
    displayModeBar: displayModeBar,
    responsive: true,
  });
  if (spec.frames) {
    addFrames(_plot, spec.frames);
  }
  _plot.on("plotly_restyle", onPlotRestyle);
}

const resizeObserver = new ResizeObserver(() => {
  if (container.value) {
    relayout(container.value, makeLayout());
  }
});

onMounted(() => {
  if (container.value) {
    resizeObserver.observe(container.value);
    buildPlot(container.value, spec.data);
  }
});

onBeforeUnmount(() => {
  if (container.value) {
    resizeObserver.unobserve(container.value);
    removePlotListeners();
    purge(container.value);
  }
});

watch(
  () => spec.data,
  async (newData, oldData) => {
    if (!isDeepEqual(newData, oldData)) {
      if (container.value) {
        await buildPlot(container.value, newData);
      }
    }
  }
);
</script>

<template>
  <div class="plotly-widget" ref="container"></div>
</template>

<style scoped>
.plotly-widget {
  width: 100%;
  max-height: 100%;
}
</style>
